/**
 * postLoginRedirect
 *
 * Polls for the CSRF cookie to confirm cookies are ready after login,
 * then redirects to the returnUrl query parameter or /dashboard.
 *
 * Shared by both login() and loginWithSession() to avoid duplication.
 */
import { authLogger } from '@/core/utils/logger';

const MAX_WAIT_MS = 2000;
const POLL_INTERVAL_MS = 50;

function hasCsrfCookie(): boolean {
  return document.cookie.split('; ').some((c) => c.startsWith('csrf-token='));
}

export async function postLoginRedirect(): Promise<void> {
  if (typeof window === 'undefined') return;

  // Poll for csrf-token cookie (non-httpOnly) to confirm cookies are ready
  const startTime = Date.now();
  while (!hasCsrfCookie() && Date.now() - startTime < MAX_WAIT_MS) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }

  if (hasCsrfCookie()) {
    authLogger.debug(
      '[postLoginRedirect] Cookies confirmed ready after',
      Date.now() - startTime,
      'ms'
    );
  } else {
    authLogger.warn(
      '[postLoginRedirect] Cookie polling timed out, proceeding anyway'
    );
  }

  // Check for return URL
  const params = new URLSearchParams(window.location.search);
  const returnUrl = params.get('returnUrl');
  const target = returnUrl ? decodeURIComponent(returnUrl) : '/dashboard';
  window.location.href = target;
}
